import { createSelector } from 'reselect';
import shipmentsSelectors from 'state/shipments/selectors';
import loadsSelectors from 'state/loads/selectors';
import { selectorsByType } from 'state/order';
import { methods as dateAndTimeMethods } from 'utils/dateAndTimes';
import { methods as addressesMethods } from 'utils/addresses';
import { constants as extrasConstants } from 'utils/extras';
import { constants as orderConstants, appointmentRequired } from 'utils/order';
import utils from 'state/direct-order/shipments/utils';
import { getDayDiff } from 'utils/dateAndTimes/methods/methods';

const getState = (state) => state.order;
const getGlobalState = (state) => state;

const getExternalProps = (_, shipmentKey, stopType, timeParam) => ({
  shipmentKey,
  stopType,
  timeParam,
});

const getIsRecurringOrder = createSelector(
  getState,
  (order) => order.isRecurring
);

const getIsEconomyModeEnabled = createSelector(
  getState,
  (order) => order.isEconomyModeEnabled
);

const getValidations = createSelector(getState, (order) => order.validations);

const getTypeOfOrder = createSelector(
  getState,
  getIsRecurringOrder,
  (order, isRecurring) => `${order.type}${isRecurring ? 'Recurring' : ''}`
);

const getShipmentKeys = createSelector(
  getGlobalState,
  getTypeOfOrder,
  getIsRecurringOrder,
  (state, typeOfOrder) =>
    state &&
    state[typeOfOrder] &&
    state[typeOfOrder].data &&
    (state[typeOfOrder].data.shipments || [])
);

const getMaxItemsInThisLoad = createSelector(
  getState,
  (_, shipmentKey, loadKey) => ({ shipmentKey, loadKey }),
  (order, { shipmentKey, loadKey }) =>
    order.maximumOfItemsInLoads
      ? order.maximumOfItemsInLoads[shipmentKey] &&
        order.maximumOfItemsInLoads[shipmentKey][loadKey]
      : undefined
);

const getMaxItemsInLoads = createSelector(
  getState,
  (order) => order.maximumOfItemsInLoads
);

const getMaxReached = createSelector(getState, (order) => order.maxReached);

const getOrderExtras = createSelector(
  getGlobalState,
  (_, orderType) => ({ orderType }),
  (state, { orderType }) => {
    return state[orderType]?.data?.extras;
  }
);

const getExtras = createSelector(
  getGlobalState,
  getTypeOfOrder,
  (state, typeOfOrder) => {
    const extras =
      state &&
      state[typeOfOrder] &&
      state[typeOfOrder].data &&
      (state[typeOfOrder].data.extras || []);
    return [...extras];
  }
);

const getRepeatedFrom = createSelector(
  getGlobalState,
  getState,
  (state, order) =>
    selectorsByType.repeatedFrom[order.type].getRepeatedFrom(state)
);

const getStopsInOrder = createSelector(
  getGlobalState,
  getTypeOfOrder,
  getShipmentKeys,
  (state, typeOfOrder, shipmentsKeys) => {
    const shipments = (shipmentsKeys || []).map((shipmentKey) => {
      const shipment = shipmentsSelectors.getShipment(state, shipmentKey);
      return {
        ...shipment,
        loads: [...loadsSelectors.getLoadsFilled(state, shipmentKey)],
      };
    });

    // TODO - implement this method in MR in other selectors
    return selectorsByType.shipments[typeOfOrder].getStops({ shipments });
  }
);

const getLoadsInForm = createSelector(
  shipmentsSelectors.getState,
  loadsSelectors.getState,
  getShipmentKeys,
  (shipments, loadsObject, shipmentsKeysInForm) => {
    const shipmentsInForm = shipmentsKeysInForm.reduce(
      (acc, shipmentKey) => ({
        ...acc,
        [shipmentKey]: { ...shipments[shipmentKey] },
      }),
      {}
    );
    return Object.entries(shipmentsInForm).reduce(
      (acc, [shipmentKey, shipment]) => [
        ...acc,
        ...shipment.loads?.map((loadKey) => ({
          ...loadsObject[loadKey],
          shipmentKey,
        })),
      ],
      []
    );
  }
);

const maxNumberOfItemsInLoadsBetweenShipments = createSelector(
  shipmentsSelectors.getState,
  loadsSelectors.getState,
  getShipmentKeys,
  (shipments, loadsObject, shipmentsKeysInForm) => {
    return shipmentsKeysInForm.reduce((acc, shipmentKey) => {
      const itemsPerShipment = shipments[shipmentKey].loads
        .map((loadKey) => loadsObject[loadKey])
        .reduce(
          (numberOfItems, load) =>
            numberOfItems + ((load && load.number_of_items) || 0),
          0
        );
      return itemsPerShipment > acc ? itemsPerShipment : acc;
    }, 0);
  }
);

const getShipmentsWithLoadsInForm = createSelector(
  shipmentsSelectors.getState,
  loadsSelectors.getState,
  getShipmentKeys,
  (shipments, loadsObject, shipmentsKeysInForm) => {
    return shipmentsKeysInForm.reduce(
      (acc, shipmentKey) => ({
        ...acc,
        [shipmentKey]: {
          ...shipments[shipmentKey],
          loads: shipments[shipmentKey].loads.reduce(
            (loadsAcc, loadKey) => ({
              ...loadsAcc,
              [loadKey]: { ...loadsObject[loadKey] },
            }),
            {}
          ),
        },
      }),
      {}
    );
  }
);

const getShipmentsOnlyWithLoadsInForm = createSelector(
  shipmentsSelectors.getState,
  loadsSelectors.getState,
  getShipmentKeys,
  (shipments, loadsObject, shipmentsKeysInForm) => {
    return shipmentsKeysInForm.reduce((acc, shipmentKey) => {
      const loadsInShipment = shipments[shipmentKey].loads;
      return {
        ...acc,
        [shipmentKey]: {
          ...loadsInShipment.reduce(
            (loadsReduce, loadKey) => ({
              ...loadsReduce,
              [loadKey]: { ...loadsObject[loadKey] },
            }),
            {}
          ),
        },
      };
    }, {});
  }
);

const getInsuredQuantity = createSelector(
  getGlobalState,
  getTypeOfOrder,
  (state, typeOfOrder) =>
    selectorsByType.shipments[typeOfOrder].getInsuredQuantity(state)
);

const prepareShipmentWithTimesForBackend = ({ shipment }) => ({
  ...shipment,
  pickup_time_from: shipment.pickup_time_from
    ? dateAndTimeMethods.joinTimeAndDate(
        shipment.pickup_time_from,
        shipment.pickup_date_from,
        addressesMethods.getTimezoneOfAddress(shipment.address_from) ||
          shipment.pickup_timezone
      )
    : undefined,
  pickup_time_to: shipment.pickup_time_to
    ? dateAndTimeMethods.joinTimeAndDate(
        shipment.pickup_time_to,
        shipment.pickup_date_to,
        addressesMethods.getTimezoneOfAddress(shipment.address_from) ||
          shipment.pickup_timezone
      )
    : undefined,
  dropoff_time_from: shipment.dropoff_time_from
    ? dateAndTimeMethods.joinTimeAndDate(
        shipment.dropoff_time_from,
        shipment.dropoff_date_from,
        addressesMethods.getTimezoneOfAddress(shipment.address_to) ||
          shipment.dropoff_timezone
      )
    : undefined,
  dropoff_time_to: shipment.dropoff_time_to
    ? dateAndTimeMethods.joinTimeAndDate(
        shipment.dropoff_time_to,
        shipment.dropoff_date_to,
        addressesMethods.getTimezoneOfAddress(shipment.address_to) ||
          shipment.dropoff_timezone
      )
    : undefined,
});

const prepareShipmentForBackend = ({
  shipment,
  loadsObject = {},
  insuredQuantity = false,
  orderType,
}) => {
  return {
    ...shipment,
    ...prepareShipmentWithTimesForBackend({ shipment }),
    loads: (shipment.loads || [])
      .map((loadKey) => loadsObject[loadKey])
      .filter((load) => load.number_of_items > 0),
    ...(insuredQuantity ? { insured_for: insuredQuantity } : {}),
    dropoff_appointment_required: !!appointmentRequired({
      orderType,
      stopType: 'dropoff',
      poi_info: shipment?.address_to?.poi_info,
      appointment_on_demand: shipment.dropoff_appointment_on_demand,
    }),
  };
};

const getShipmentsForBackend = createSelector(
  shipmentsSelectors.getState,
  loadsSelectors.getState,
  getShipmentKeys,
  getInsuredQuantity,
  getIsRecurringOrder,
  getExtras,
  getTypeOfOrder,
  (
    shipments,
    loadsObject,
    shipmentsKeysInForm,
    insuredQuantity,
    isRecurring,
    orderExtras,
    orderType
  ) => {
    return shipmentsKeysInForm.reduce((acc, shipmentKey) => {
      const shipmentExtras = shipments[shipmentKey].extras;

      const hasHandlingLoading = shipmentsKeysInForm.map(
        (key) =>
          shipments[key].extras.indexOf(extrasConstants.HANDLING_LOADING) >= 0
      );
      const hasHandlingUnloading = shipmentsKeysInForm.map(
        (key) =>
          shipments[key].extras.indexOf(extrasConstants.HANDLING_UNLOADING) >= 0
      );

      // Add extras when it's marked
      if (
        orderType === orderConstants.TYPES.DISTRIBUTION &&
        hasHandlingLoading[0] &&
        shipmentExtras.indexOf(extrasConstants.HANDLING_LOADING) < 0
      ) {
        shipmentExtras.push(extrasConstants.HANDLING_LOADING);
      } else if (
        orderType === orderConstants.TYPES.COLLECTION &&
        hasHandlingUnloading[0] &&
        shipmentExtras.indexOf(extrasConstants.HANDLING_UNLOADING) < 0
      ) {
        shipmentExtras.push(extrasConstants.HANDLING_UNLOADING);
      }

      // Remove extras when it's not marked
      if (
        orderType === orderConstants.TYPES.DISTRIBUTION &&
        !hasHandlingLoading[0] &&
        shipmentExtras.indexOf(extrasConstants.HANDLING_LOADING) >= 0
      ) {
        shipmentExtras.splice(
          shipmentExtras.indexOf(extrasConstants.HANDLING_LOADING),
          1
        );
      } else if (
        orderType === orderConstants.TYPES.COLLECTION &&
        !hasHandlingUnloading[0] &&
        shipmentExtras.indexOf(extrasConstants.HANDLING_UNLOADING) >= 0
      ) {
        shipmentExtras.splice(
          shipmentExtras.indexOf(extrasConstants.HANDLING_UNLOADING),
          1
        );
      }

      const shipment = {
        ...prepareShipmentForBackend({
          shipment: {
            ...shipments[shipmentKey],
            pickup_comment_from_shipper_to_driver:
              shipments[shipmentKey].pickup_comment_from_shipper_to_driver ||
              '',
            dropoff_comment_from_shipper_to_driver:
              shipments[shipmentKey].dropoff_comment_from_shipper_to_driver ||
              '',
            internal_reference: shipments[shipmentKey].internal_reference || '',
            extras: [
              ...shipmentExtras,
              ...(insuredQuantity && insuredQuantity > 0 ? ['INSURANCE'] : []),
              ...(isRecurring ? orderExtras : []),
            ],
          },
          loadsObject,
          ...(insuredQuantity ? { insuredQuantity } : {}),
          orderType,
        }),
      };

      return {
        ...acc,
        [shipmentKey]: {
          ...shipment,
        },
      };
    }, {});
  }
);

const getShipmentsForQuote = (state) => getShipmentsForBackend(state);
const getShipmentsForCreateOrder = (state) => getShipmentsForBackend(state);

const getMinAndMaxForTimes = createSelector(
  getGlobalState,
  getTypeOfOrder,
  getExternalProps,
  (state, typeOfOrder, { shipmentKey, stopType, timeParam }) => {
    return selectorsByType.shipments[typeOfOrder].getMinAndMaxForTimes(
      state,
      shipmentKey,
      stopType,
      timeParam
    );
  }
);

const getSuggestedTimes = createSelector(
  getMinAndMaxForTimes,
  ({ minMax = {}, date }) => {
    const { min = {}, max = {} } = minMax;
    const minRounded = utils.getMinRounded(min.value);
    const maxRounded = utils.getMaxRounded(max.value);
    const diffDaysWithCurrentDate = getDayDiff(minRounded, date);

    return utils.getHoursRangeByStepOfMinutes({
      fromDateTime: minRounded,
      toDateTime: maxRounded,
      stepInMinutes: 30,
      diffDaysWithCurrentDate,
    });
  }
);

const getSuggestedTimesObjects = createSelector(
  getSuggestedTimes,
  (suggestedTimes) => suggestedTimes
);

const getPaymentIntentSecret = createSelector(
  getState,
  (order) => order.paymentIntentSecret
);
const getLastId = createSelector(getState, (order) => order.lastId);

const getIsEditing = createSelector(getState, (order) => order.isEditing);

const getOriginalPrice = createSelector(
  getState,
  (order) => order.originalPrice
);

const getOriginalId = createSelector(getState, (order) => order.originalId);
const getShipperQuoteId = createSelector(
  getState,
  (order) => order.shipperQuoteId
);

const getDiffPriceWhenEdit = createSelector(
  getGlobalState,
  getOriginalPrice,
  getTypeOfOrder,
  (state, originalPrice, typeOfOrder) => {
    const quote = selectorsByType.quote[typeOfOrder].getQuote(state);

    const originalTotalPrice =
      originalPrice &&
      originalPrice.net_full_price &&
      Number(originalPrice.net_full_price);
    const quoteTotalPrice =
      quote && quote.net_full_price && Number(quote.net_full_price);

    let diff = 0;
    if (!Number.isNaN(quoteTotalPrice) && !Number.isNaN(originalTotalPrice)) {
      diff = quoteTotalPrice - originalTotalPrice;
    }
    return diff;
  }
);

const getIsQuotingWithZipCode = createSelector(
  shipmentsSelectors.getState,
  getShipmentKeys,
  (shipments, shipmentsKeysInForm) => {
    return (shipmentsKeysInForm || []).some((key) => {
      const shipment = shipments[key];
      if (shipment && shipment.address_from && shipment.address_from.isZipcode)
        return true;
      return shipment && shipment.address_to && shipment.address_to.isZipcode;
    });
  }
);

const getHolidays = createSelector(
  getState,
  getExternalProps,
  (order, { stopType, shipmentKey }) => {
    if (!order) return null;
    if (!order.holidays[shipmentKey]) return null;
    if (!order.holidays[shipmentKey][stopType]) return null;

    return order.holidays[shipmentKey][stopType];
  }
);

const getQuoteSuggestions = createSelector(getState, (order) => {
  return order?.quoteSuggestions || [];
});

const getTimeWindows = createSelector(
  getState,
  (order) => order.timeWindows || null
);

const getTimeWindowsByShipmentKeys = createSelector(
  getTimeWindows,
  getShipmentKeys,
  (timeWindows, shipmentKeys) => {
    // We are considering that the shipment keys order is the same than the time windows
    return shipmentKeys.reduce((current, shipmentKey, i) => {
      return {
        ...current,
        [shipmentKey]: timeWindows[i],
      };
    }, {});
  }
);

const getTimeWindowByShipmentKey = createSelector(
  getTimeWindowsByShipmentKeys,
  getExternalProps,
  (timeWindows, { shipmentKey }) => {
    // We bypass collections
    return shipmentKey.indexOf('MR_COLLECTION') >= 0
      ? null
      : timeWindows?.[shipmentKey];
  }
);

const getTimeWindowsRequestPayload = createSelector(
  getState,
  (order) => order.timeWindowsRequestPayload || null
);

export default {
  getState,
  getTypeOfOrder,
  getIsRecurringOrder,
  getIsEconomyModeEnabled,

  getExternalProps,

  getShipmentKeys,

  getMaxItemsInThisLoad,
  getMaxReached,
  getExtras,
  getOrderExtras,
  getMaxItemsInLoads,

  getRepeatedFrom,
  getStopsInOrder,

  getLoadsInForm,
  maxNumberOfItemsInLoadsBetweenShipments,
  getShipmentsWithLoadsInForm,
  getShipmentsOnlyWithLoadsInForm,

  getShipmentsForBackend,
  getShipmentsForQuote,
  getShipmentsForCreateOrder,

  getSuggestedTimesObjects,
  getPaymentIntentSecret,
  getLastId,

  getIsEditing,
  getOriginalPrice,
  getOriginalId,
  getShipperQuoteId,
  prepareShipmentForBackend, // maybe we should move it,
  prepareShipmentWithTimesForBackend, // maybe we should move it,
  getDiffPriceWhenEdit,

  getIsQuotingWithZipCode,

  getHolidays,
  getValidations,
  getQuoteSuggestions,
  getTimeWindows,
  getTimeWindowByShipmentKey,
  getTimeWindowsRequestPayload,
};
